import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Divider,
  Link,
  CardContent,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import PersonIcon from "@mui/icons-material/Person";

import { StyledCard } from "../common/StyledCard";
import { SupplierSummaryDto } from "../../shared/dtos/supplier/SupplierSummaryDto";
import { SupplierResponseDto } from "../../shared/dtos/supplier/SupplierResponseDto";

interface SupplierCardProps {
  supplier: SupplierSummaryDto | SupplierResponseDto;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const SupplierCard: React.FC<SupplierCardProps> = ({
  supplier,
  onView,
  onEdit,
  onDelete,
}) => {
  return (
    <StyledCard>
      <CardContent
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box sx={{ maxWidth: "70%" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {supplier.name}
            </Typography>
            {supplier.productCategory && (
              <Chip
                label={supplier.productCategory}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{
                  mt: 0.5,
                  height: 20,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                }}
              />
            )}
          </Box>
          <Stack direction="row">
            {onView && (
              <Tooltip title="View Details">
                <IconButton size="small" color="info" onClick={onView}>
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Edit Supplier">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete Supplier">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Body Details */}
        <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
          {supplier.contactPerson && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <PersonIcon fontSize="small" sx={{ color: "action.active" }} />
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <b>Contact:</b> {supplier.contactPerson}
              </Typography>
            </Stack>
          )}

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <PhoneIcon fontSize="small" sx={{ color: "action.active" }} />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {supplier.phoneNumber}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <EmailIcon fontSize="small" sx={{ color: "action.active" }} />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {supplier.email}
            </Typography>
          </Stack>

          {supplier.website && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <LanguageIcon fontSize="small" sx={{ color: "action.active" }} />
              <Link
                href={supplier.website}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                color="info"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Visit Website
              </Link>
            </Stack>
          )}
        </Stack>

        {/* Footer Tax ID */}
        {supplier.taxId && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              Tax ID: {supplier.taxId}
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default SupplierCard;
